import { useMutation } from '@apollo/client';
import {
  Banner,
  FieldLabel,
  FlexBox,
  FormField,
  FormGrid,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { Path, SubmitHandler, useFormContext } from 'react-hook-form';

import {
  CreateOAuthProviderArg,
  createOAuthProviderMutation,
  CreateOAuthProviderResponse,
  OAuthProvider,
  OAuthStrategy,
  OAuthStrategySchemaField,
  UpdateOAuthProviderArg,
  updateOAuthProviderMutation,
  UpdateOAuthProviderResponse,
} from 'apis/partnerIntegrations';
import { HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';

import { OAuthFormData, OAuthFormDataField } from '../../oAuth.types';

import { FormActions } from './components/actions/actions';
import { SchemaField } from './components/schemaField/schemaField';
import styles from './oAuthForm.module.scss';
import { mapSchemaToSettings } from './utils/mapProviderToSchema/mapProviderToSchema';

type OAuthFormProps = {
  disabled: boolean;
  selectedStrategy: OAuthStrategy;
  oAuthSchema: readonly OAuthStrategySchemaField[];
  oAuthProvider?: OAuthProvider;
  onChangeParentOAuthId: (id: string) => void;
  setOAuthProvider: Dispatch<SetStateAction<OAuthProvider | undefined>>;
  handleParentSubmit: () => void;
};

/** Represents the OAuth form. */
export const OAuthForm = ({
  disabled,
  selectedStrategy,
  oAuthSchema,
  oAuthProvider,
  onChangeParentOAuthId,
  setOAuthProvider,
  handleParentSubmit,
}: OAuthFormProps): JSX.Element => {
  const notify = useNotifications();
  const { control, handleSubmit, reset } = useFormContext<OAuthFormData>();

  const hasSchemaFields = oAuthSchema.length > 0;
  const oAuthConnectionId = oAuthProvider?.id;
  const hasProvider = !!oAuthProvider;
  const hasNewStrategy = selectedStrategy.name !== oAuthProvider?.strategy;
  const hasFormValues = hasNewStrategy ? false : hasProvider;

  const [createOAuthProvider, { loading: isCreateOAuthProviderLoading }] =
    useMutation<CreateOAuthProviderResponse, CreateOAuthProviderArg>(
      createOAuthProviderMutation,
      {
        onError: (err) => {
          notify(
            {
              title: 'Error while creating the OAuth provider.',
              text: err,
            },
            NotificationType.Error,
          );
        },
        onCompleted: (result) => {
          const provider = result.Management.createOAuthProvider;

          handleOnComplete(provider);

          // Update the OAuth provider hook state with the new provider
          setOAuthProvider(provider);

          // Update the custom card form state with the new provider id
          onChangeParentOAuthId(provider.id);

          // Also save the custom card with the new provider id
          handleParentSubmit();

          ampli.settingsIntegrationsCustomCardOauthProviderCreated({
            oAuthProviderId: provider.id,
            oAuthStrategy: selectedStrategy.name,
          });
        },
      },
    );

  const [updateOAuthProvider, { loading: isUpdateOAuthProviderLoading }] =
    useMutation<UpdateOAuthProviderResponse, UpdateOAuthProviderArg>(
      updateOAuthProviderMutation,
      {
        onError: (err) => {
          notify(
            {
              title: 'Error while updating the OAuth provider.',
              text: err,
            },
            NotificationType.Error,
          );
        },
        onCompleted: (result) => {
          handleOnComplete(result.Management.updateOAuthProvider);

          const provider = result.Management.updateOAuthProvider;

          ampli.settingsIntegrationsCustomCardOauthProviderUpdated({
            oAuthProviderId: provider.id,
            oAuthStrategy: selectedStrategy.name,
          });
        },
      },
    );

  const handleOnComplete = (provider: OAuthProvider) => {
    const { name, id, settings } = provider;

    reset({
      ...mapSchemaToSettings({
        schema: oAuthSchema,
        settings,
      }),
      strategy: selectedStrategy.name,
      name,
      id,
    });

    notify(
      {
        title: 'Success',
        text: `OAuth provider ${name} successfully ${
          hasProvider ? 'updated' : 'created'
        }.`,
        icon: 'i-done',
        time: 5000,
      },
      NotificationType.Success,
    );
  };

  const onSubmit: SubmitHandler<OAuthFormData> = async (formValues) => {
    const { name, id, ...settings } = formValues;

    // We only want to updated, when there is already a provider and the strategy is not changed
    if (hasProvider && !hasNewStrategy) {
      updateOAuthProvider({
        variables: {
          id: id as string,
          name,
          settings: Object.keys(settings)
            // Filtering out unchanged secrets
            .filter((key) => (settings[key] as OAuthFormDataField)?.isEditing)
            .map((key) => ({
              key,
              value: (settings[key] as OAuthFormDataField).value,
              secret: (settings[key] as OAuthFormDataField).secret,
            })),
        },
      });
    } else {
      createOAuthProvider({
        variables: {
          name,
          strategy: selectedStrategy.name,
          settings: Object.keys(settings).map((key) => ({
            key,
            value: (settings[key] as OAuthFormDataField).value,
            secret: (settings[key] as OAuthFormDataField).secret,
          })),
        },
      });
    }
  };

  // Effect to populate the form state
  useEffect(() => {
    if (hasProvider) {
      const { name, id, settings } = oAuthProvider;

      // Reset the form if the strategy got changed
      if (hasNewStrategy) {
        reset({
          ...mapSchemaToSettings({
            schema: oAuthSchema,
          }),
          name: '',
          id: undefined,
        });
      } else {
        //...otherwise populate the form with the provider data
        reset({
          ...mapSchemaToSettings({
            schema: oAuthSchema,
            settings,
          }),
          name,
          id,
        });
      }
    } else if (hasSchemaFields) {
      const formSchema = mapSchemaToSettings({ schema: oAuthSchema });

      // Sets the form state based on the schema with empty values
      reset(formSchema);
    }
  }, [
    hasProvider,
    hasNewStrategy,
    hasSchemaFields,
    oAuthProvider,
    oAuthSchema,
    reset,
  ]);

  return (
    <FlexBox flexDirection="column" gap="200" margin="0 0 300 0">
      <FieldLabel>{`${selectedStrategy.label} configuration`}</FieldLabel>

      <FormGrid className={styles.formWrapper}>
        {!hasSchemaFields && (
          <FormField>
            This strategy does not have any input fields defined.
          </FormField>
        )}

        {hasSchemaFields && (
          <>
            <FormField>
              <HookFormText
                disabled={disabled}
                label="Name"
                name={'name' as Path<OAuthFormData>}
                control={control}
                componentProps={{
                  placeholder: 'Insert name',
                }}
                rules={{
                  required: {
                    value: true,
                    message: 'Please add a name for your OAuth provider',
                  },
                }}
              />
            </FormField>

            {oAuthSchema.map((schema) => (
              <SchemaField
                key={schema.key}
                schema={schema}
                disabled={disabled}
                hasFormValues={hasFormValues}
              />
            ))}

            {hasProvider && !oAuthConnectionId && (
              <FormField>
                <Banner
                  variant="warning"
                  headline="Unsaved custom card"
                  description={
                    <>
                      You have to save the custom card in order to pair the card
                      with the OAuth provider.
                    </>
                  }
                />
              </FormField>
            )}

            <FormField>
              <FormActions
                disabled={disabled}
                hasFormValues={hasFormValues}
                isSaving={
                  isCreateOAuthProviderLoading || isUpdateOAuthProviderLoading
                }
                onSubmit={handleSubmit(onSubmit)}
                oAuthProviderId={oAuthConnectionId}
                oAuthStrategy={selectedStrategy.name}
              />
            </FormField>
          </>
        )}
      </FormGrid>
    </FlexBox>
  );
};
